import { useOpenModal } from '@flowus/common/next-modal';
import { CollectionViewType } from '@next-space/fe-api-idl';
import type { FC, MouseEvent } from 'react';
import { useCallback } from 'react';
import { Icon } from 'src/common/components/icon';
import { Tooltip } from 'src/common/components/tooltip';
import { SHARE_LINK, UNTITLED } from 'src/common/const';
import { segmentsToText } from 'src/editor/utils/editor';
import { useCollectionView } from 'src/hooks/collection-view/use-collection-view';
import { usePayInfo } from 'src/hooks/page/use-pay-info';
import { useCurrentUser } from 'src/hooks/user/use-current-user';
import { useIsLogin } from 'src/hooks/user/use-is-login';
import { writeTextInClipboard } from 'src/utils/clipboard';
import { usePickBlock } from 'src/utils/pick-block';
import { useOpenLogin } from '../empty/open-login';
import { useOpenWeappQR } from '../weapp-qr';
import { useGetSharePageUrl } from './helper';
import { useOpenAICover, useOpenShareLinkQr } from './share-link-qr';
import { useOpenSharePoster } from './share-poster';
import { ShareDialog } from './share-dialog';
import { Button } from '@flowus/common/components';
import { useEnableAI } from 'src/hooks/block/use-enable-AI';
import { cx } from '@flowus/common/cx';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';

interface ShareButtonsProps {
  uuid: string;
  url: string;
  onCopyLink?: () => void;
  onCopyEmbedLink?: () => void;
}
export const ShareButtons: FC<ShareButtonsProps> = (props) => {
  const { url, onCopyLink, uuid, onCopyEmbedLink } = props;
  const block = usePickBlock(uuid, ['data'], ['segments']);
  const collectionView = useCollectionView(uuid);
  const openWeappQR = useOpenWeappQR();
  const openShareLinkQr = useOpenShareLinkQr();
  const openSharePoster = useOpenSharePoster();
  const openAICover = useOpenAICover();
  const { enableAI } = useEnableAI();
  const { t } = useAppTranslation();

  let title = `${t('common.already_shared')}${segmentsToText(block?.data.segments) ?? UNTITLED}`;
  if (collectionView?.type === CollectionViewType.FORM) {
    title = t('share.shareButtons.inviteFillForm');
  }

  return (
    <div className="px-4 border-t border-grey6 text-t2 bg-grey9 flex items-center  h-12">
      {__BUILD_IN__ ? (
        <span className="mr-4 text-black">{t('share.shareButtons.shareMethod')}</span>
      ) : (
        <Button
          colorType="grey"
          className={cx('border border-grey6 bg-transparent rounded-[20px]', {
            hidden: !enableAI,
          })}
          onClick={() => {
            openAICover();
          }}
        >
          <Icon name="IcShare" size="middle" className="text-normal/65" />
          <div className="text-grey2 text-t4-medium ml-0.5">
            {t('share.shareButtons.shareAIPoster')}
          </div>
        </Button>
      )}
      <Tooltip popup={SHARE_LINK} className="animate-click mr-2 ml-2 rounded-full border p-1">
        <Icon name="IcLink" size="middle" className="text-normal/65" onClick={onCopyLink} />
      </Tooltip>
      {onCopyEmbedLink && (
        <Tooltip
          popup={t('share.shareButtons.embedModeShare')}
          className="animate-click mr-2 rounded-full border p-1"
        >
          <Icon
            name="IcGlobeShare"
            size="middle"
            className="text-normal/65"
            onClick={onCopyEmbedLink}
          />
        </Tooltip>
      )}
      {!(__BUILD_IN__ || __PRIVATE__) && (
        <Tooltip
          popup={t('share.shareButtons.miniProgramShare')}
          onClick={() => openWeappQR({ url })}
          className="animate-click mr-2 rounded-full border p-1"
        >
          <Icon name="IcMenuMiniProgramQrcode" size="middle" className="text-normal/65" />
        </Tooltip>
      )}
      {!__BUILD_IN__ && (
        <Tooltip
          popup={t('common.generate_poster')}
          className="animate-click mr-2 rounded-full border p-1"
          onClick={() => openSharePoster({ url, pageId: uuid })}
        >
          <Icon name="IcImage" size="middle" className="text-normal/65" />
        </Tooltip>
      )}
      <Tooltip
        popup={t('share.shareButtons.qrCodeShare')}
        className="animate-click rounded-full border p-1"
        onClick={(e) => openShareLinkQr(e, { url, title })}
      >
        <Icon name="IcQrCode" size="middle" className="text-normal/65" />
      </Tooltip>
    </div>
  );
};

export const useOpenShareLinkList = (uuid: string) => {
  const openLogin = useOpenLogin();
  const isLogin = useIsLogin();
  const openModal = useOpenModal();

  return useCallback(
    async (event: MouseEvent) => {
      if (!isLogin) {
        openLogin();
        return;
      }

      openModal.dropdown({
        popcorn: event?.currentTarget,
        placement: 'bottom',
        content: () => {
          return <ShareDialog pageId={uuid} />;
        },
      });
    },
    [isLogin, openLogin, openModal, uuid]
  );
};
